import { Close, Search } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import * as Yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import integrationService from "../../../service/integrationService";
import { toast } from "react-toastify";
import errorHandler from "../../../utils/errorHandler";
import publicService from "../../../service/publicService";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const SelectTaxes = ({ isOpen, onClose, setCurrentStep, id, integration, currentStep, refetch }) => {
  const defaultValues = { taxes: [], vat: { code: "ZERO_VAT", percent: 0, value: "Zero Value-Added Tax" } };

  const { handleSubmit, formState, control, reset, watch } = useForm({
    mode: "all",
    resolver: yupResolver(
      Yup.object({
        taxes: Yup.array(
          Yup.object({
            code: Yup.string().required(),
            value: Yup.string().required(),
          })
        ),
        vat: Yup.object({
          code: Yup.string().required(),
          value: Yup.string().required(),
        }).required(),
      })
    ),
    defaultValues: defaultValues,
  });

  const taxesQuery = useQuery({
    enabled: currentStep === 4,
    queryKey: ["default-firs-taxes"],
    queryFn: () => publicService.getTaxes({}),
  });

  const _taxes = watch("taxes");

  const mutation = useMutation({
    mutationFn: (data) => integrationService.proceed(data, { integrationId: id, organizationId: integration?.organizationId }),
    onSuccess: ({ data }) => {
      refetch();
      toast.success("Taxes added successfully...");
      onClose();
      setCurrentStep(1);
    },
    onError: ({ response }) => errorHandler(response),
  });

  const save = (data) => {
    let percent = 0;
    if (!isNaN(Number(data?.vat?.percent)) && typeof Number(data?.vat?.percent) === "number") {
      percent = Number(data?.vat?.percent);
    }
    mutation.mutate({
      vat: { percentage: percent, name: data?.vat?.code },
      taxes: data?.taxes?.map((item) => {
        let percent = 0;
        if (!isNaN(Number(item?.percent)) && typeof Number(item?.percent) === "number") {
          percent = Number(item?.percent);
        }
        return { percentage: percent, name: item?.code };
      }),
    });
  };

  const errors = formState.errors;
  const taxes = taxesQuery?.data?.data?.data?.taxes;
  const vat = taxesQuery?.data?.data?.data?.vat;

  React.useEffect(() => {
    if (!isOpen) return;
    reset({ taxes: [], vat: defaultValues.vat });
  }, [id, isOpen, taxes?.length, vat?.length]);

  if (currentStep !== 4) return null;

  return (
    <Dialog
      onClose={onClose}
      open={isOpen}
      maxWidth="xs"
      PaperProps={{ className: "w-100", component: "form", onSubmit: handleSubmit(save) }}
    >
      <React.Fragment>
        <div className="d-flex justify-content-between align-items-center gap-4 p-3 py-2 pe-2">
          <div className="fs-5 fw-medium">Select Taxes</div>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </div>
        <Divider className="bg-secondary-subtle" />
      </React.Fragment>

      <DialogContent>
        <Box mb={3}>
          <label>
            Select VAT:{" "}
            <Typography component="span" color="error">
              *
            </Typography>
          </label>
          <Controller
            name="vat"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth error={!!errors["vat"]}>
                <Autocomplete
                  id="tags-standard"
                  options={vat}
                  defaultValue={defaultValues.vat}
                  getOptionLabel={(option) => option?.value}
                  onChange={(e, value) => field.onChange(value)}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        <Radio color="secondary" style={{ marginRight: 8 }} checked={selected} />
                        {option?.value}
                      </li>
                    );
                  }}
                  renderInput={(params) => <TextField {...params} size="small" variant="outlined" placeholder="Select Taxes" />}
                />
                {errors["vat"] ? <FormHelperText className="">{errors["vat"]?.message}</FormHelperText> : null}
              </FormControl>
            )}
          />
        </Box>
        <Box mb={3}>
          <label>Select Taxes:</label>
          <Controller
            name="taxes"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth error={!!errors["taxes"]}>
                <Autocomplete
                  multiple
                  id="tags-standard"
                  options={taxes}
                  getOptionLabel={(option) => option?.value}
                  onChange={(e, value) => field.onChange(value)}
                  value={_taxes}
                  disableCloseOnSelect
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        <Checkbox color="secondary" icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                        {option?.value}
                      </li>
                    );
                  }}
                  renderInput={(params) => <TextField {...params} size="small" variant="outlined" placeholder="Select Taxes" />}
                />
                {errors["taxes"] ? <FormHelperText className="">{errors["taxes"]?.message}</FormHelperText> : null}
              </FormControl>
            )}
          />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ gap: 2 }}>
        <Button variant="contained" type="submit" color="secondary" disabled={mutation.isPending} style={{ minWidth: 120 }}>
          {mutation.isPending ? "Submitting..." : "Complete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectTaxes;
